import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Plus, Trash2, Check, Ship, Users, Shield, ShieldCheck, ShieldAlert, UserMinus, UserPlus, Clock, CheckCircle, XCircle, ImagePlus, ChefHat, Settings2, Eye, Pencil, Ban, KeyRound, FileDown, Search, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ROLE_LABELS = { owner: "Eier", admin: "Admin", cook: "Admin", crew: "Mannskap", guest: "Gjest" };
const ROLE_ICONS = { owner: ShieldAlert, admin: ShieldCheck, cook: ShieldCheck, crew: Shield, guest: Eye };
const ACTION_LABELS = { add: "Legg til", update: "Endre", delete: "Slett" };

const PAGES = [
  { id: "plan", label: "Turnusplan" },
  { id: "baking", label: "Baking" },
  { id: "recipes", label: "Oppskrifter" },
  { id: "pantry", label: "Beholdning" },
  { id: "shopping", label: "Handleliste" },
  { id: "my-lists", label: "Mine Lister" },
  { id: "scan", label: "Skann kvittering" },
  { id: "trips", label: "Turer" },
  { id: "waste", label: "Matsvinn" },
];

const ACCESS_OPTIONS = [
  { value: "none", label: "Ingen", icon: Ban, color: "text-stone-400" },
  { value: "read", label: "Kun lese", icon: Eye, color: "text-blue-600" },
  { value: "write", label: "Lese + skrive", icon: Pencil, color: "text-navy-800" },
];

function defaultAccessForRole(role) {
  if (role === "owner" || role === "admin" || role === "cook") return Object.fromEntries(PAGES.map((p) => [p.id, "write"]));
  if (role === "crew") return Object.fromEntries(PAGES.map((p) => [p.id, "write"]));
  if (role === "guest") return Object.fromEntries(PAGES.map((p) => [p.id, "read"]));
  return Object.fromEntries(PAGES.map((p) => [p.id, "none"]));
}

export default function BoatsPage() {
  const { user, loadActiveBoat } = useAuth();
  const [boats, setBoats] = useState([]);
  const [activeBoat, setActiveBoat] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [pending, setPending] = useState([]);
  const [accessEditor, setAccessEditor] = useState(null); // { boatId, memberId }
  const [shiftAccounts, setShiftAccounts] = useState(null); // { boatName, accounts }
  const [newUserDialog, setNewUserDialog] = useState(null); // { boatId, boatName }
  const [showDeleteCode, setShowDeleteCode] = useState({}); // { [boatId]: boolean }
  const [deleteBoatDialog, setDeleteBoatDialog] = useState(null); // { boatId, boatName, expectedCode }
  const [deleteBoatPin, setDeleteBoatPin] = useState("");
  const [newUserForm, setNewUserForm] = useState({ username: "", password: "", name: "", role: "crew", preferred_shift: "" });
  const [passwordDialog, setPasswordDialog] = useState(null); // { boatId, memberId, memberName }
  const [newPassword, setNewPassword] = useState("");
  const [usernameDialog, setUsernameDialog] = useState(null); // { boatId, memberId, memberName, currentUsername }
  const [newUsername, setNewUsername] = useState("");
  const [kind, setKind] = useState("boat"); // "boat" | "household"
  const [address, setAddress] = useState("");
  const [householdDays, setHouseholdDays] = useState(7);
  const [numOnboard, setNumOnboard] = useState(6);
  const [filterKind, setFilterKind] = useState("all"); // "all" | "boat" | "household"
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMembers, setExpandedMembers] = useState({}); // { [boatId]: bool }
  const [openCards, setOpenCards] = useState({}); // { [boatId]: bool } — mobile-only card expand

  const load = useCallback(async () => {
    try {
      const [boatsRes, activeRes] = await Promise.all([api.getBoats(), api.getActiveBoat()]);
      setBoats(boatsRes.data);
      setActiveBoat(activeRes.data);
      if (activeRes.data?.id) {
        setSelectedBoat(activeRes.data.id);
        const pendRes = await api.getPendingChanges(activeRes.data.id);
        setPending(pendRes.data);
      }
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadPending = async (boatId) => {
    try { const res = await api.getPendingChanges(boatId); setPending(res.data); } catch {}
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      const payload = { name: name.trim(), kind, num_onboard: Number(numOnboard) || 6 };
      if (kind === "household") {
        payload.address = address.trim();
        payload.household_days = Number(householdDays) || 7;
      }
      const res = await api.createBoat(payload);
      await api.activateBoat(res.data.id);
      toast.success(`${name} opprettet og aktivert!`);
      // Show shift accounts if returned (only for kind=boat)
      const accounts = res.data?.shift_accounts;
      if (accounts && accounts.length) {
        setShiftAccounts({ boatName: name.trim(), accounts });
      }
      setCreateOpen(false); setName(""); setAddress(""); setHouseholdDays(7); setNumOnboard(6); setKind("boat"); load();
    } catch { toast.error("Feil ved oppretting"); }
  };

  const handleActivate = async (id) => {
    try {
      await api.activateBoat(id);
      toast.success("Båt byttet!");
      await loadActiveBoat();
      load();
    } catch { toast.error("Feil"); }
  };

  const handleDelete = async (boat) => {
    setDeleteBoatDialog({ boatId: boat.id, boatName: boat.name, expectedCode: boat.delete_code || "1234" });
    setDeleteBoatPin("");
  };
  const confirmDeleteBoat = async () => {
    if (!deleteBoatDialog) return;
    if (deleteBoatPin !== deleteBoatDialog.expectedCode) {
      toast.error("Feil kode");
      return;
    }
    try {
      await api.deleteBoat(deleteBoatDialog.boatId);
      toast.success("Båt slettet");
      setDeleteBoatDialog(null);
      setDeleteBoatPin("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const handleRoleChange = async (boatId, memberId, newRole) => {
    try {
      await api.updateMemberRole(boatId, memberId, { role: newRole });
      toast.success("Rolle oppdatert");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const handleAccessChange = async (boatId, memberId, pageAccess) => {
    try {
      await api.updateMemberAccess(boatId, memberId, { page_access: pageAccess });
      toast.success("Tilgang oppdatert");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const handleCreateUser = async () => {
    if (!newUserDialog) return;
    const { username, password, name, role, preferred_shift } = newUserForm;
    if (!username.trim() || !password.trim()) { toast.error("Brukernavn og passord er påkrevd"); return; }
    try {
      await api.adminCreateMember(newUserDialog.boatId, { username: username.trim(), password: password.trim(), name: name.trim(), role, preferred_shift });
      toast.success(`Bruker '${username.trim()}' opprettet`);
      setNewUserDialog(null);
      setNewUserForm({ username: "", password: "", name: "", role: "crew", preferred_shift: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const handleChangePassword = async () => {
    if (!passwordDialog) return;
    if (newPassword.trim().length < 4) { toast.error("Passord må være minst 4 tegn"); return; }
    try {
      await api.adminSetMemberPassword(passwordDialog.boatId, passwordDialog.memberId, { password: newPassword.trim() });
      toast.success("Passord endret");
      setPasswordDialog(null); setNewPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const handleChangeUsername = async () => {
    if (!usernameDialog) return;
    const val = newUsername.trim().toLowerCase();
    if (val.length < 3) { toast.error("Brukernavn må være minst 3 tegn"); return; }
    if (!/^[a-z0-9._-]+$/.test(val)) { toast.error("Kun små bokstaver, tall, punktum, bindestrek og understrek"); return; }
    if (val === (usernameDialog.currentUsername || "").toLowerCase()) { toast.info("Ingen endring"); return; }
    try {
      await api.adminSetMemberUsername(usernameDialog.boatId, usernameDialog.memberId, { username: val });
      toast.success("Brukernavn endret");
      setUsernameDialog(null); setNewUsername("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const handleExportCsv = async (boatId, boatName) => {
    try {
      toast.loading("Genererer CSV...", { id: "csv-export" });
      const res = await api.exportMembersCsv(boatId);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const safe = (boatName || "baat").replace(/[^a-zA-Z0-9-_]/g, "_");
      const a = document.createElement("a");
      a.href = url; a.download = `medlemmer-${safe}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV lastet ned", { id: "csv-export" });
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke eksportere", { id: "csv-export" }); }
  };

  const handleRemoveMember = async (boatId, memberId) => {
    try {
      await api.removeMember(boatId, memberId);
      toast.success("Medlem fjernet");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const handleApprove = async (changeId) => {
    if (!activeBoat) return;
    try {
      await api.approveChange(activeBoat.id, changeId);
      toast.success("Godkjent!");
      loadPending(activeBoat.id);
    } catch { toast.error("Feil"); }
  };

  const handleReject = async (changeId) => {
    if (!activeBoat) return;
    try {
      await api.rejectChange(activeBoat.id, changeId);
      toast.success("Avvist");
      loadPending(activeBoat.id);
    } catch { toast.error("Feil"); }
  };

  const handleShiftConfig = async (boatId, shift, patch) => {
    try {
      // Find existing shift config so we can merge instead of overwriting
      const boat = boats.find((b) => b.id === boatId);
      const existing = boat?.[shift] || {};
      const merged = {
        start_date: existing.start_date || "",
        cycle_days: existing.cycle_days || 21,
        first_trip_number: existing.first_trip_number || 1,
        ...patch,
      };
      const data = {};
      data[shift] = merged;
      await api.updateShiftConfig(boatId, data);
      toast.success(`${shift === "shift1" ? "Skift 1" : "Skift 2"} oppdatert`);
      load();
      await loadActiveBoat();
    } catch { toast.error("Feil ved lagring"); }
  };

  const handleDeleteCodeSave = async (boatId, code) => {
    const cleaned = (code || "").trim();
    if (!/^[0-9]{3,8}$/.test(cleaned)) {
      toast.error("Koden må være 3-8 tall");
      return;
    }
    try {
      await api.updateBoatDeleteCode(boatId, cleaned);
      toast.success("Slettekode oppdatert");
      await loadActiveBoat();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil ved lagring"); }
  };

  const handleImageUpload = async (boatId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.uploadBoatImage(boatId, formData);
      toast.success("Båtbilde oppdatert!");
      await loadActiveBoat();
      load();
    } catch { toast.error("Feil ved opplasting"); }
  };

  const myRole = activeBoat?.my_role || "";
  const isAdmin = myRole === "owner" || myRole === "admin" || myRole === "cook";
  const myMemberData = activeBoat?.members?.find((m) => m.user_id === activeBoat?.owner_id) || {};

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="boats-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Båter & Husstander</h1>
          <p className="text-sm text-stone-500 mt-1">Administrer båter, mannskap og tilganger</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="create-boat-btn">
            <Plus className="h-4 w-4" /> Ny båt / hushold
          </button>
        </div>
      </div>

      {/* Search + filter tabs: All / Boats / Households */}
      {boats.length > 3 && (
        <div className="mb-4 relative" data-testid="boats-search-wrapper">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Søk etter navn eller adresse…"
            className="w-full rounded-lg border border-stone-200 bg-white pl-9 pr-3 py-2.5 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
            data-testid="boats-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-xs"
              data-testid="clear-search-btn"
            >
              Nullstill
            </button>
          )}
        </div>
      )}

      {/* Filter tabs: All / Boats / Households – visible when user has any of both kinds */}
      {boats.length > 0 && (() => {
        const boatCount = boats.filter((b) => (b.kind || "boat") === "boat").length;
        const houseCount = boats.filter((b) => b.kind === "household").length;
        if (boatCount === 0 || houseCount === 0) return null; // Skip when only one kind exists
        return (
          <div className="flex gap-2 mb-6" data-testid="kind-filter-tabs">
            <button
              onClick={() => setFilterKind("all")}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors border ${filterKind === "all" ? "bg-navy-800 text-white border-navy-800" : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"}`}
              data-testid="filter-all"
            >
              Alle <span className="text-[10px] opacity-70 font-bold">{boats.length}</span>
            </button>
            <button
              onClick={() => setFilterKind("boat")}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors border ${filterKind === "boat" ? "bg-navy-800 text-white border-navy-800" : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"}`}
              data-testid="filter-boats"
            >
              ⚓️ Båter <span className="text-[10px] opacity-70 font-bold">{boatCount}</span>
            </button>
            <button
              onClick={() => setFilterKind("household")}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors border ${filterKind === "household" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"}`}
              data-testid="filter-households"
            >
              🏠 Husstander <span className="text-[10px] opacity-70 font-bold">{houseCount}</span>
            </button>
          </div>
        );
      })()}

      {/* Boat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {boats
          .filter((b) => filterKind === "all" || (b.kind || "boat") === filterKind)
          .filter((b) => {
            if (!searchQuery.trim()) return true;
            const q = searchQuery.trim().toLowerCase();
            return (b.name || "").toLowerCase().includes(q) || (b.address || "").toLowerCase().includes(q);
          })
          .map((boat) => {
          const isActive = activeBoat?.id === boat.id;
          // Per-boat admin determination: look up current user's role on THIS boat
          const myMemberOnBoat = boat.members?.find((m) => m.user_id === user?.user_id) || null;
          const myRoleOnBoat = myMemberOnBoat?.role || "";
          const isAdminOnBoat = ["owner", "admin", "cook"].includes(myRoleOnBoat) || user?.role === "admin";
          return (
            <div key={boat.id} className={`bg-white border rounded-xl overflow-hidden transition-all ${isActive ? "border-seawater-500 ring-1 ring-navy-100" : "border-stone-100"}`} data-testid={`boat-card-${boat.id}`}>
              {/* Boat image header */}
              <div className="relative h-28 bg-stone-100 overflow-hidden">
                {boat.image_url ? (
                  <img src={boat.image_url} alt={boat.name} className="w-full h-full object-cover opacity-30" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-50 to-stone-200">
                    <Ship className="h-12 w-12 text-stone-300" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-stone-800 flex items-center gap-1.5">
                      {boat.kind === "household" && <span className="text-sm" title="Hjemme-hushold">🏠</span>}
                      {boat.name}
                    </h3>
                    <span className="text-xs text-stone-500">{(boat.members || []).filter((m) => !m.is_global_admin).length} medlemmer{boat.kind === "household" && boat.address ? ` · ${boat.address}` : ""}</span>
                  </div>
                  <div className="flex gap-1">
                    <label className="p-1.5 rounded-md bg-white/80 hover:bg-white text-stone-500 hover:text-seawater-600 cursor-pointer transition-colors" title="Last opp bilde" data-testid={`upload-image-${boat.id}`}>
                      <ImagePlus className="h-4 w-4" />
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(boat.id, e)} />
                    </label>
                    {!isActive && (
                      <button onClick={() => handleActivate(boat.id)} className="p-1.5 rounded-md bg-white/80 hover:bg-white text-stone-500 hover:text-seawater-600 transition-colors" title="Bytt til denne båten" data-testid={`activate-boat-${boat.id}`}>
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                    {(() => {
                      const myMemberInBoat = boat.members?.find((m) => m.user_id === activeBoat?.owner_id || m.user_id === user?.user_id);
                      const isBoatOwnerOrAdmin = myMemberInBoat && ["owner", "admin", "cook"].includes(myMemberInBoat.role);
                      const isGlobalAdmin = user?.role === "admin";
                      return (isBoatOwnerOrAdmin || isGlobalAdmin) ? (
                        <button onClick={() => handleDelete(boat)} className="p-1.5 rounded-md bg-white/80 hover:bg-white text-stone-500 hover:text-red-500 transition-colors" data-testid={`delete-boat-${boat.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : null;
                    })()}
                    {/* Mobile-only expand/collapse toggle */}
                    <button
                      onClick={() => setOpenCards((p) => ({ ...p, [boat.id]: !p[boat.id] }))}
                      className="md:hidden p-1.5 rounded-md bg-navy-800 text-white hover:bg-navy-900 transition-colors"
                      data-testid={`toggle-card-${boat.id}`}
                      aria-label={openCards[boat.id] ? "Skjul detaljar" : "Vis detaljar"}
                    >
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openCards[boat.id] ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                </div>
              </div>
              <div className={`p-4 ${openCards[boat.id] ? "block" : "hidden md:block"}`}>

              {isActive && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-navy-800 bg-sailcloth-100 px-2 py-0.5 rounded-full mb-3" data-testid={`active-badge-${boat.id}`}>{boat.kind === "household" ? "Aktiv husstand" : "Aktiv båt"}</span>}

              {/* Admin settings — collapsible group (skift-rotasjon, slettekode, opptelling-terskel) */}
              {isAdminOnBoat && (
                <details className="mb-3 rounded-lg border border-stone-200 bg-stone-50/40 group" data-testid={`settings-panel-${boat.id}`}>
                  <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-stone-700 flex items-center justify-between hover:bg-stone-100 rounded-lg [&::-webkit-details-marker]:hidden">
                    <span className="inline-flex items-center gap-1.5">
                      <Settings2 className="h-3.5 w-3.5 text-stone-500" /> Innstillingar
                    </span>
                    <span className="text-[10px] text-stone-400 truncate ml-2">
                      {(boat.kind || "boat") === "boat" ? "Skift · Slettekode · Opptelling" : "Slettekode · Opptelling"}
                    </span>
                  </summary>
                  <div className="p-3 pt-1 space-y-3">

              {/* Shift config - only for boats, not households */}
              {(boat.kind || "boat") === "boat" && (
                <div className="p-3 bg-white rounded-lg space-y-2 border border-stone-100" data-testid={`shift-config-${boat.id}`}>
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Skift-rotasjon</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-stone-500 block mb-0.5">Skift 1 — startdato</label>
                      <input type="date" defaultValue={boat.shift1?.start_date || ""} onBlur={(e) => handleShiftConfig(boat.id, "shift1", { start_date: e.target.value })} className="w-full rounded border border-stone-200 bg-white px-2 py-1 text-xs focus:border-navy-800 outline-none" data-testid={`shift1-date-${boat.id}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-stone-500 block mb-0.5">Tur-nr ved start</label>
                      <input type="number" min="1" max="99" defaultValue={boat.shift1?.first_trip_number || 1} onBlur={(e) => handleShiftConfig(boat.id, "shift1", { first_trip_number: parseInt(e.target.value, 10) || 1 })} className="w-full rounded border border-stone-200 bg-white px-2 py-1 text-xs focus:border-navy-800 outline-none" data-testid={`shift1-trip-num-${boat.id}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-stone-500 block mb-0.5">Skift 2 — startdato</label>
                      <input type="date" defaultValue={boat.shift2?.start_date || ""} onBlur={(e) => handleShiftConfig(boat.id, "shift2", { start_date: e.target.value })} className="w-full rounded border border-stone-200 bg-white px-2 py-1 text-xs focus:border-navy-800 outline-none" data-testid={`shift2-date-${boat.id}`} />
                    </div>
                    <div>
                      <label className="text-[10px] text-stone-500 block mb-0.5">Tur-nr ved start</label>
                      <input type="number" min="1" max="99" defaultValue={boat.shift2?.first_trip_number || 1} onBlur={(e) => handleShiftConfig(boat.id, "shift2", { first_trip_number: parseInt(e.target.value, 10) || 1 })} className="w-full rounded border border-stone-200 bg-white px-2 py-1 text-xs focus:border-navy-800 outline-none" data-testid={`shift2-trip-num-${boat.id}`} />
                    </div>
                  </div>
                  <p className="text-[10px] text-stone-400">21 dager per tur — neste tur-nr beregnes automatisk fra startdato</p>
                </div>
              )}

              {/* Extra meal slots (Tilbehør-faner) removed per user request.
                  Users can still add per-day extras via the day menu in WeeklyPlan. */}

              {/* Delete-all PIN (admin) */}
              <div className="p-3 bg-red-50/40 rounded-lg space-y-2 border border-red-100" data-testid={`delete-code-${boat.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1">
                      <KeyRound className="h-3 w-3" /> Slettekode (bekreft tømming av beholdning)
                    </p>
                    <button
                      onClick={() => setShowDeleteCode((prev) => ({ ...prev, [boat.id]: !prev[boat.id] }))}
                      className="text-[10px] font-medium text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
                      data-testid={`toggle-show-delete-code-${boat.id}`}
                    >
                      <Eye className="h-3 w-3" />
                      {showDeleteCode[boat.id] ? "Skjul" : "Vis"}
                    </button>
                  </div>
                  <div className="flex gap-1.5">
                    <input
                      key={`delcode-${boat.id}-${boat.delete_code}`}
                      type={showDeleteCode[boat.id] ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={8}
                      defaultValue={boat.delete_code || "1234"}
                      onBlur={(e) => { if (e.target.value !== (boat.delete_code || "1234")) handleDeleteCodeSave(boat.id, e.target.value); }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="flex-1 rounded border border-stone-200 bg-white px-2 py-1 text-sm font-mono tracking-widest focus:border-red-500 outline-none"
                      data-testid={`delete-code-input-${boat.id}`}
                    />
                  </div>
                  <p className="text-[10px] text-stone-500">Kreves for å slette hele beholdningen. Kun tall (3-8 siffer). Tab ut for å lagre.</p>
                </div>

              {/* Pantry verification threshold (admin) */}
              <div className="p-3 bg-emerald-50/40 rounded-lg space-y-2 border border-emerald-100" data-testid={`verify-threshold-${boat.id}`}>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                    Beholdnings-opptelling: terskel
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      key={`vt-${boat.id}-${boat.pantry_verification_threshold_days}`}
                      type="number"
                      min={3}
                      max={90}
                      defaultValue={boat.pantry_verification_threshold_days || 14}
                      onBlur={async (e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isFinite(v) || v < 3 || v > 90) {
                          toast.error("Terskel må være mellom 3 og 90 dager");
                          return;
                        }
                        if (v === (boat.pantry_verification_threshold_days || 14)) return;
                        try {
                          await api.updateBoat(boat.id, { pantry_verification_threshold_days: v });
                          toast.success(`Terskel satt til ${v} dager`);
                          load();
                          await loadActiveBoat();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Feil ved lagring");
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="w-20 rounded border border-stone-200 bg-white px-2 py-1 text-sm focus:border-emerald-500 outline-none"
                      data-testid={`verify-threshold-input-${boat.id}`}
                    />
                    <span className="text-xs text-stone-500">dager før varen markeres som "trenger opptelling"</span>
                  </div>
                  <p className="text-[10px] text-stone-500">Standard: 14 dager. Lavere = strengare påminnelser. Tab ut for å lagre.</p>
                </div>

                  </div>
                </details>
              )}

              {/* Members */}
              <div className="space-y-1.5">
                {isAdminOnBoat && (
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-stone-100">
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Medlemmer</p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleExportCsv(boat.id, boat.name)}
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-stone-600 hover:text-stone-900"
                        title="Last ned CSV med medlemmer"
                        data-testid={`export-csv-btn-${boat.id}`}
                      >
                        <FileDown className="h-3 w-3" /> CSV
                      </button>
                      <button
                        onClick={() => setNewUserDialog({ boatId: boat.id, boatName: boat.name, boatKind: boat.kind || "boat" })}
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-navy-800 hover:text-navy-900"
                        data-testid={`add-user-btn-${boat.id}`}
                      >
                        <UserPlus className="h-3 w-3" /> Ny bruker
                      </button>
                    </div>
                  </div>
                )}
                {(() => {
                  const allMembers = boat.members || [];
                  const isExpanded = expandedMembers[boat.id];
                  const shouldCollapse = allMembers.length > 5 && !isExpanded;
                  const visibleMembers = shouldCollapse ? allMembers.slice(0, 5) : allMembers;
                  return (
                    <>
                    {visibleMembers.map((m) => {
                  const RoleIcon = ROLE_ICONS[m.role] || Shield;
                  const isOwner = m.role === "owner";
                  const editingAccess = accessEditor && accessEditor.boatId === boat.id && accessEditor.memberId === m.user_id;
                  const currentAccess = (m.page_access && Object.keys(m.page_access).length > 0) ? { ...defaultAccessForRole(m.role), ...m.page_access } : defaultAccessForRole(m.role);
                  return (
                    <div key={m.user_id} className="rounded-lg hover:bg-stone-50 transition-colors" data-testid={`member-${m.user_id}`}>
                      <div className="flex items-center justify-between py-1.5 px-2 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <RoleIcon className={`h-3.5 w-3.5 ${m.role === "owner" ? "text-amber-500" : m.role === "admin" || m.role === "cook" ? "text-seawater-600" : m.role === "guest" ? "text-blue-500" : "text-stone-400"}`} />
                          <span className="text-sm text-stone-700">{m.name || m.email}</span>
                          <span className="text-[10px] text-stone-400 uppercase">{ROLE_LABELS[m.role] || m.role}</span>
                          {m.has_shared_key && <span className="text-[9px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"><KeyRound className="h-2.5 w-2.5" /> Delt nøkkel</span>}
                        </div>
                        {isAdminOnBoat && !isOwner && (
                          <div className="flex items-center gap-1">
                            <Select value={m.role === "cook" ? "admin" : m.role} onValueChange={(v) => handleRoleChange(boat.id, m.user_id, v)}>
                              <SelectTrigger className="h-7 w-28 text-xs border-stone-200" data-testid={`role-select-${m.user_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="crew">Mannskap</SelectItem>
                                <SelectItem value="guest">Gjest</SelectItem>
                              </SelectContent>
                            </Select>
                            <button
                              onClick={() => setAccessEditor(editingAccess ? null : { boatId: boat.id, memberId: m.user_id })}
                              className={`p-1.5 rounded transition-colors ${editingAccess ? "bg-sailcloth-100 text-navy-800" : "hover:bg-stone-100 text-stone-400 hover:text-stone-600"}`}
                              title="Tilpass sidetilganger"
                              data-testid={`access-editor-toggle-${m.user_id}`}
                            >
                              <Settings2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => setPasswordDialog({ boatId: boat.id, memberId: m.user_id, memberName: m.name || m.email })}
                              className="p-1.5 rounded hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition-colors"
                              title="Endre passord"
                              data-testid={`change-password-${m.user_id}`}
                            >
                              <KeyRound className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => { setUsernameDialog({ boatId: boat.id, memberId: m.user_id, memberName: m.name || m.email, currentUsername: m.username || "" }); setNewUsername(m.username || ""); }}
                              className="p-1.5 rounded hover:bg-sky-50 text-stone-400 hover:text-sky-600 transition-colors"
                              title="Endre brukernavn"
                              data-testid={`change-username-${m.user_id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {boat.kind === "household" && (
                              <button
                                onClick={async () => {
                                  try {
                                    await api.updateMemberRole(boat.id, m.user_id, { has_shared_key: !m.has_shared_key });
                                    await load();
                                    toast.success(m.has_shared_key ? "Delt nøkkel fjernet" : "Delt nøkkel gitt");
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Feil");
                                  }
                                }}
                                className={`p-1 rounded transition-colors ${m.has_shared_key ? "bg-amber-50 text-amber-700" : "hover:bg-stone-100 text-stone-400"}`}
                                title={m.has_shared_key ? "Fjern delt nøkkel" : "Gi delt nøkkel"}
                                data-testid={`toggle-shared-key-${m.user_id}`}
                              >
                                <KeyRound className="h-3 w-3" />
                              </button>
                            )}
                            <button onClick={() => handleRemoveMember(boat.id, m.user_id)} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 transition-colors">
                              <UserMinus className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      {editingAccess && (
                        <div className="ml-7 mr-2 mb-2 p-3 rounded-lg bg-white border border-stone-200 shadow-sm" data-testid={`access-matrix-${m.user_id}`}>
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Sidetilganger</p>
                            <button
                              onClick={() => { handleAccessChange(boat.id, m.user_id, {}); setAccessEditor(null); }}
                              className="text-[10px] text-stone-400 hover:text-stone-600 underline"
                              data-testid={`reset-access-${m.user_id}`}
                            >
                              Nullstill til rollestandard
                            </button>
                          </div>
                          <div className="space-y-1.5">
                            {PAGES.map((pg) => {
                              const lvl = currentAccess[pg.id] || "none";
                              return (
                                <div key={pg.id} className="flex items-center justify-between gap-2">
                                  <span className="text-xs text-stone-700 w-32">{pg.label}</span>
                                  <div className="flex items-center gap-1">
                                    {ACCESS_OPTIONS.map((opt) => {
                                      const OptIcon = opt.icon;
                                      const selected = lvl === opt.value;
                                      return (
                                        <button
                                          key={opt.value}
                                          onClick={() => {
                                            const next = { ...currentAccess, [pg.id]: opt.value };
                                            handleAccessChange(boat.id, m.user_id, next);
                                          }}
                                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all ${selected ? "bg-stone-900 text-white" : `bg-stone-50 ${opt.color} hover:bg-stone-100`}`}
                                          title={opt.label}
                                          data-testid={`access-${m.user_id}-${pg.id}-${opt.value}`}
                                        >
                                          <OptIcon className="h-3 w-3" /> {opt.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                    {allMembers.length > 5 && (
                      <button
                        onClick={() => setExpandedMembers((p) => ({ ...p, [boat.id]: !isExpanded }))}
                        className="w-full mt-1 inline-flex items-center justify-center gap-1 rounded-md border border-stone-200 bg-stone-50 hover:bg-stone-100 px-2 py-1.5 text-[11px] font-medium text-stone-600 transition-colors"
                        data-testid={`toggle-members-${boat.id}`}
                      >
                        <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        {isExpanded ? "Skjul" : `Vis alle ${allMembers.length} medlemmar`}
                      </button>
                    )}
                    </>
                  );
                })()}
              </div>
              </div>
            </div>
          );
        })}
        {boats.length === 0 && (
          <div className="col-span-full text-center py-16">
            <Ship className="h-12 w-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ingen båter ennå</h3>
            <p className="text-sm text-stone-400 mt-1">Opprett en ny båt for å komme i gang</p>
          </div>
        )}
        {boats.length > 0 && searchQuery.trim() && boats.filter((b) => filterKind === "all" || (b.kind || "boat") === filterKind).filter((b) => {
          const q = searchQuery.trim().toLowerCase();
          return (b.name || "").toLowerCase().includes(q) || (b.address || "").toLowerCase().includes(q);
        }).length === 0 && (
          <div className="col-span-full text-center py-12" data-testid="no-search-results">
            <Search className="h-10 w-10 mx-auto text-stone-300 mb-3" />
            <p className="text-sm text-stone-500">Ingen treff for &quot;<strong>{searchQuery}</strong>&quot;</p>
            <button onClick={() => setSearchQuery("")} className="mt-3 text-xs text-navy-800 hover:underline">Nullstill søk</button>
          </div>
        )}
      </div>

      {/* Pending changes (approval queue) */}
      {isAdmin && pending.length > 0 && (
        <div className="mb-8" data-testid="pending-changes">
          <h2 className="text-xl font-semibold text-stone-800 mb-4 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Clock className="h-5 w-5 text-amber-500" /> Ventende godkjenninger ({pending.length})
          </h2>
          <div className="space-y-2">
            {pending.map((c) => (
              <div key={c.id} className="bg-white border border-amber-200 rounded-xl p-4 flex items-center justify-between" data-testid={`pending-${c.id}`}>
                <div>
                  <span className="text-sm font-medium text-stone-700">{c.user_name}</span>
                  <span className="text-xs text-stone-400 ml-2">vil {ACTION_LABELS[c.action]?.toLowerCase()} i {c.collection}</span>
                  {c.item_data?.name && <span className="text-xs font-medium text-stone-600 ml-1">"{c.item_data.name}"</span>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleApprove(c.id)} className="inline-flex items-center gap-1 rounded-md bg-navy-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-navy-900 transition-colors" data-testid={`approve-${c.id}`}>
                    <CheckCircle className="h-3.5 w-3.5" /> Godkjenn
                  </button>
                  <button onClick={() => handleReject(c.id)} className="inline-flex items-center gap-1 rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid={`reject-${c.id}`}>
                    <XCircle className="h-3.5 w-3.5" /> Avvis
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm" data-testid="create-boat-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {kind === "household" ? "Nytt hushold" : "Ny båt"}
            </DialogTitle>
            <DialogDescription>
              {kind === "household"
                ? "Opprett et hjemme-hushold med oppskrifter og handleliste for hele familien"
                : "Opprett en ny båt med eget lager og handlelister"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Kind selector */}
            <div className="grid grid-cols-2 gap-2" data-testid="kind-selector">
              <button
                type="button"
                onClick={() => setKind("boat")}
                className={`flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 text-xs font-semibold transition-all ${kind === "boat" ? "border-navy-800 bg-navy-50 text-navy-800" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                data-testid="kind-boat"
              >
                <span className="text-xl">⚓️</span>
                Båt
                <span className="text-[10px] font-normal text-stone-500">Med turnus og skift</span>
              </button>
              <button
                type="button"
                onClick={() => setKind("household")}
                className={`flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 text-xs font-semibold transition-all ${kind === "household" ? "border-amber-600 bg-amber-50 text-amber-800" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                data-testid="kind-household"
              >
                <span className="text-xl">🏠</span>
                Hjemme
                <span className="text-[10px] font-normal text-stone-500">For familien</span>
              </button>
            </div>

            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === "household" ? "Navn på hushold (f.eks. Familien Hansen)" : "Båtnavn (f.eks. Frøy Spirit)"}
              className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
              data-testid="boat-name-input"
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Antall i husstand</label>
                <input
                  type="number" min="1" max="30"
                  value={numOnboard}
                  onChange={(e) => setNumOnboard(e.target.value)}
                  className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-navy-800"
                  data-testid="boat-num-onboard"
                />
              </div>
              {kind === "household" && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Plan (dager)</label>
                  <input
                    type="number" min="1" max="60"
                    value={householdDays}
                    onChange={(e) => setHouseholdDays(e.target.value)}
                    className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-navy-800"
                    data-testid="household-days"
                  />
                </div>
              )}
            </div>

            {kind === "household" && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Husadresse (vises til medlemmer)</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Storgata 5, 5000 Bergen"
                  className="mt-1 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-navy-800"
                  data-testid="household-address"
                />
              </div>
            )}

            <button
              onClick={handleCreate}
              className={`w-full rounded-md px-4 py-2.5 text-sm font-medium text-white transition-colors ${kind === "household" ? "bg-amber-600 hover:bg-amber-700" : "bg-navy-800 hover:bg-navy-900"}`}
              data-testid="save-boat-btn"
            >
              {kind === "household" ? "Opprett hushold" : "Opprett båt"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift accounts dialog (shown once after boat creation) */}
      <Dialog open={!!shiftAccounts} onOpenChange={(o) => { if (!o) setShiftAccounts(null); }}>
        <DialogContent className="max-w-md" data-testid="shift-accounts-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Skift-kontoer opprettet</DialogTitle>
            <DialogDescription>
              Disse to kontoene er automatisk opprettet for {shiftAccounts?.boatName}. Del dem med mannskapet — passordene vises kun én gang.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(shiftAccounts?.accounts || []).map((acc) => (
              <div key={acc.username} className="bg-stone-50 border border-stone-200 rounded-lg p-3 space-y-1.5" data-testid={`shift-account-${acc.shift}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-navy-800">Skift {acc.shift}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Brukernavn:</span>
                  <code className="text-sm font-mono font-bold text-stone-800 bg-white px-2 py-0.5 rounded">{acc.username}</code>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Passord:</span>
                  <code className="text-sm font-mono font-bold text-stone-800 bg-white px-2 py-0.5 rounded">{acc.password}</code>
                </div>
              </div>
            ))}
            <button onClick={() => { const text = shiftAccounts.accounts.map((a) => `Skift ${a.shift}: ${a.username} / ${a.password}`).join("\n"); navigator.clipboard.writeText(text); toast.success("Kopiert!"); }} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50 transition-colors" data-testid="copy-shift-accounts-btn">
              Kopier begge
            </button>
            <button onClick={() => setShiftAccounts(null)} className="w-full rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="close-shift-accounts-btn">
              Jeg har lagret kontoene
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {/* New user dialog (admin creates user + adds as boat member) */}
      <Dialog open={!!newUserDialog} onOpenChange={(o) => { if (!o) { setNewUserDialog(null); setNewUserForm({ username: "", password: "", name: "", role: "crew", preferred_shift: "" }); } }}>
        <DialogContent className="max-w-md" data-testid="new-user-dialog">
          {(() => {
            const isHousehold = newUserDialog?.boatKind === "household";
            const containerLabel = isHousehold ? "husstanden" : "båten";
            return (
              <>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {isHousehold ? "Nytt medlem" : "Ny bruker"} – {newUserDialog?.boatName}
                  </DialogTitle>
                  <DialogDescription>
                    Opprett en ny konto og legg den til som medlem på {containerLabel}.
                    {isHousehold && " Denne brukeren får ikke tilgang til båt-funksjoner."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Brukernavn</label>
                    <input value={newUserForm.username} onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })} placeholder="f.eks. ola" className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none" data-testid="new-user-username" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Passord</label>
                    <input type="text" value={newUserForm.password} onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })} placeholder="Minst 4 tegn" className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none" data-testid="new-user-password" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Navn (valgfritt)</label>
                    <input value={newUserForm.name} onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })} placeholder="Fullt navn" className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none" data-testid="new-user-name" />
                  </div>
                  <div className={isHousehold ? "" : "grid grid-cols-2 gap-3"}>
                    <div>
                      <label className="text-xs font-medium text-stone-500 mb-1 block">Rolle</label>
                      <Select value={newUserForm.role} onValueChange={(v) => setNewUserForm({ ...newUserForm, role: v })}>
                        <SelectTrigger className="w-full" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="crew">{isHousehold ? "Medlem" : "Mannskap"}</SelectItem>
                          <SelectItem value="guest">Gjest</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!isHousehold && (
                      <div>
                        <label className="text-xs font-medium text-stone-500 mb-1 block">Skift (valgfritt)</label>
                        <Select value={newUserForm.preferred_shift || "none"} onValueChange={(v) => setNewUserForm({ ...newUserForm, preferred_shift: v === "none" ? "" : v })}>
                          <SelectTrigger className="w-full" data-testid="new-user-shift"><SelectValue placeholder="Ingen" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Ingen</SelectItem>
                            <SelectItem value="1">Skift 1</SelectItem>
                            <SelectItem value="2">Skift 2</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <button onClick={handleCreateUser} className="w-full rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="new-user-submit">
                    Opprett bruker
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={(o) => { if (!o) { setPasswordDialog(null); setNewPassword(""); } }}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Endre passord</DialogTitle>
            <DialogDescription>Sett nytt passord for {passwordDialog?.memberName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nytt passord (minst 4 tegn)" className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 outline-none" data-testid="change-password-input" onKeyDown={(e) => e.key === "Enter" && handleChangePassword()} />
            <button onClick={handleChangePassword} className="w-full rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="change-password-submit">
              Lagre nytt passord
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change username dialog */}
      <Dialog open={!!usernameDialog} onOpenChange={(o) => { if (!o) { setUsernameDialog(null); setNewUsername(""); } }}>
        <DialogContent className="max-w-sm" data-testid="change-username-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Endre brukernavn</DialogTitle>
            <DialogDescription>
              Sett nytt brukernavn for {usernameDialog?.memberName}
              {usernameDialog?.currentUsername && (
                <span className="block text-xs text-stone-400 mt-1">Nåværende: <code className="font-mono">{usernameDialog.currentUsername}</code></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
              placeholder="nytt.brukernavn"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-mono focus:border-navy-800 outline-none"
              data-testid="change-username-input"
              onKeyDown={(e) => e.key === "Enter" && handleChangeUsername()}
            />
            <p className="text-[11px] text-stone-500">Kun små bokstaver, tall, punktum, bindestrek og understrek. Minst 3 tegn. Må være unikt globalt.</p>
            <button onClick={handleChangeUsername} className="w-full rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="change-username-submit">
              Lagre nytt brukernavn
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete boat PIN confirmation */}
      <Dialog open={!!deleteBoatDialog} onOpenChange={(v) => { if (!v) { setDeleteBoatDialog(null); setDeleteBoatPin(""); } }}>
        <DialogContent className="max-w-sm" data-testid="delete-boat-pin-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Slett båt</DialogTitle>
            <DialogDescription>
              Dette sletter <strong>{deleteBoatDialog?.boatName}</strong> og alle tilhørende data (beholdning, handlelister, turer). Kan ikke angres. Skriv inn båtens slettekode for å bekrefte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              value={deleteBoatPin}
              onChange={(e) => setDeleteBoatPin(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmDeleteBoat(); }}
              placeholder="••••"
              className="w-full rounded-md border border-stone-200 bg-white px-3 py-3 text-center text-lg font-mono tracking-[0.5em] focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
              data-testid="delete-boat-pin-input"
            />
            <div className="flex gap-2">
              <button onClick={() => { setDeleteBoatDialog(null); setDeleteBoatPin(""); }} className="flex-1 rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors" data-testid="delete-boat-cancel-btn">
                Avbryt
              </button>
              <button
                onClick={confirmDeleteBoat}
                disabled={deleteBoatPin !== deleteBoatDialog?.expectedCode}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="delete-boat-confirm-btn"
              >
                <Trash2 className="h-4 w-4" /> Slett båt
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
